import React, { useMemo, useRef, useState } from 'react';
import { Typography, Box, Container, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, Checkbox, ListItemText, OutlinedInput, SelectChangeEvent, IconButton, Chip, FormHelperText, Divider, Snackbar, Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { fetchServices, addService, fetchAllEmployees, deleteService, updateService, getCurrentBusinessConfig, updatePreferencesConfig } from '../../firebase/firebase-delegate';
import { BusinessConfig, Employee, Service } from '../../types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import EditableServicesSection from '../../components/employee/services/EditableServicesSection';
import { AddCircle, Edit, RemoveCircle } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { formatMinutes } from '../../time/timeUtils';
import ImageSelect from '../../components/global/images/ImageSelector';
import { TIME_INTERVAL } from '../../constants';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { deleteField } from 'firebase/firestore';
import ConfirmationDialog from '../../components/global/ConfirmationDialog';
import theme from '../../theme/theme';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

const MAX_SERVICE_TITLE_LENGTH = 30;
const MAX_SERVICE_DESCRIPTION_LENGTH = 200;
const EMPTY_SERVICE_PLACEHOLDER: Omit<Service, 'id'> = {
    title: '',
    description: '',
    price: 0,
    time: 0,
    employees: [],
    category: ''
};

const ServicesScreen: React.FC = () => {
    const [addedServicePlaceholder, setAddedServicePlaceholder] = useState<Omit<Service, 'id'>>({ ...EMPTY_SERVICE_PLACEHOLDER });
    const [addDialogErrors, setAddDialogErrors] = useState<{ title?: string; price?: string; employees?: string; time?: string }>({});
    const [isAddServiceDialogOpen, setIsAddServiceDialogOpen] = useState(false);
    const employeesErrorRef = useRef<HTMLDivElement | null>(null);
    const [addedServiceImagePlaceholder, setAddedServiceImagePlaceholder] = useState<File | undefined>(undefined);
    const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
    const [newCategory, setNewCategory] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
    const [deleteCategoryConfirmOpen, setDeleteCategoryConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const queryClient = useQueryClient();



    // Fetch the current employee data
    const allEmployeesQuery = useQuery<Array<Employee>>({
        queryKey: ['allEmployees'],
        queryFn: () => fetchAllEmployees(),
    });

    // Fetch all services data
    const servicesQuery = useQuery<Service[]>({
        queryKey: ['services'],
        queryFn: () => fetchServices(),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    const businessConfigQuery = useQuery({
        queryKey: ['businessConfig'],
        queryFn: () => getCurrentBusinessConfig(),
    });
    const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data ?? null);
    const resourcePlural = bookingUxConfig.terminology.resourcePlural;
    const isResourceMode = bookingUxConfig.bookingModel === 'resource';
    const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
    const selectableEmployees = useMemo(() => {
        const allEmployees = allEmployeesQuery.data ?? [];
        if (!isResourceMode || !ownerId) return allEmployees;
        return allEmployees.filter((employee) => employee.id !== ownerId);
    }, [allEmployeesQuery.data, isResourceMode, ownerId]);

    const deleteServiceMutation = useMutation({
        mutationFn: async (service: Service) => {
            await deleteService(service);
            return service;
        },
        onSuccess: (service) => {
            const previous = (queryClient.getQueryData<Service[]>(['services']) || []);
            queryClient.setQueryData(
                ['services'],
                previous.filter((s) => s.id !== service.id)
            );
            setSnackbar({ open: true, message: 'Service deleted.', severity: 'success' });
        },
        onError: (error: any) => {
            console.error('Error deleting service: ', error);
            setSnackbar({ open: true, message: 'Failed to delete service.', severity: 'error' });
        },
    });


    const deleteCategoryMutation = useMutation({
        mutationFn: async (categoryToDelete: string) => {
            const config = businessConfigQuery.data;
            const services = servicesQuery.data;

            if (!config || !services) {
                throw new Error("Missing data for category deletion");
            }

            const updatedCategories = (config.preferences?.serviceCategories || []).filter(
                (cat) => cat !== categoryToDelete
            );

            const updatedConfig: BusinessConfig = {
                ...config,
                preferences: {
                    ...config.preferences,
                    serviceCategories: updatedCategories,
                },
            };

            // First: Update preferences only
            await updatePreferencesConfig(updatedConfig.preferences);

            // Second: Update services (individually)
            const servicesToUpdate = services.filter((s) => s.category === categoryToDelete);
            console.log(`Updating ${servicesToUpdate.length} services to remove category: ${categoryToDelete}`);

            for (const service of servicesToUpdate) {
                await updateService(service, {
                    ...service,
                    category: deleteField() as unknown as undefined // type hack if needed
                });
            }
            return categoryToDelete;
        },
        onSuccess: async (deletedCategory) => {
            const currentConfig = businessConfigQuery.data;
            if (currentConfig) {
                queryClient.setQueryData(['businessConfig'], {
                    ...currentConfig,
                    preferences: {
                        ...currentConfig.preferences,
                        serviceCategories: (currentConfig.preferences?.serviceCategories || []).filter(
                            (cat) => cat !== deletedCategory
                        ),
                    },
                });
            }
            const currentServices = queryClient.getQueryData<Service[]>(['services']) || [];
            queryClient.setQueryData(
                ['services'],
                currentServices.map((s) =>
                    s.category === deletedCategory ? { ...s, category: undefined } : s
                )
            );
            if (selectedCategory === deletedCategory) {
                setSelectedCategory(null);
            }
            setSnackbar({ open: true, message: 'Category deleted.', severity: 'success' });
        },
        onError: (error) => {
            console.error("Failed to delete category:", error);
            setSnackbar({ open: true, message: 'Failed to delete category.', severity: 'error' });
        },
    });



    const updateServiceCategoriesMutation = useMutation({
        mutationFn: async (categories: string[]) => {
            const config = businessConfigQuery.data;
            if (!config) throw new Error("No config loaded");

            const updatedConfig: BusinessConfig = {
                ...config,
                preferences: {
                    ...config.preferences,
                    serviceCategories: categories,
                }
            };

            await updatePreferencesConfig(updatedConfig.preferences);
            return categories;
        },
        onSuccess: (_data, categories) => {
            const currentConfig = businessConfigQuery.data;
            if (currentConfig) {
                queryClient.setQueryData(['businessConfig'], {
                    ...currentConfig,
                    preferences: {
                        ...currentConfig.preferences,
                        serviceCategories: categories,
                    },
                });
            } else {
                queryClient.setQueryData(['businessConfig'], {
                    preferences: { serviceCategories: categories },
                } as BusinessConfig);
            }
            setSnackbar({ open: true, message: 'Categories updated.', severity: 'success' });


        },
        onError: (error) => {
            console.error('Failed to update categories:', error);
            setSnackbar({ open: true, message: 'Failed to update categories.', severity: 'error' });
        },
        onSettled: () => {
            setIsAddCategoryDialogOpen(false);
            setNewCategory('');
        }
    });


    // TODO!!! fix this when we make appointments have all the data.
    const onSaveMutation = useMutation({
        mutationFn: async ({ oldService, updatedService, serviceImage }: { oldService: Service; updatedService: Service; serviceImage?: File }) => {
            await updateService(oldService, updatedService, serviceImage);
            return updatedService;
        },
        onSuccess: (updatedService) => {
            const previous = (queryClient.getQueryData<Service[]>(['services']) || []);
            queryClient.setQueryData(
                ['services'],
                previous.map((s) => (s.id === updatedService.id ? updatedService : s))
            );
            setSnackbar({ open: true, message: 'Service updated.', severity: 'success' });
        },
        onError: (e) => {
            console.log('Failed to save service. ' + e);
            setSnackbar({ open: true, message: 'Failed to update service.', severity: 'error' });
        },
    });

    const onAddServiceMutation = useMutation({
        mutationFn: async () => {
            const createdId = await addService(addedServicePlaceholder, addedServiceImagePlaceholder);
            return { ...addedServicePlaceholder, id: createdId } as Service;
        },
        onSuccess: (createdService) => {
            const previous = (queryClient.getQueryData<Service[]>(['services']) || []);
            queryClient.setQueryData(
                ['services'],
                [...previous, createdService as Service]
            );
            setSnackbar({ open: true, message: 'Service added.', severity: 'success' });
        },
        onError: () => {
            console.log('Failed to add service');
            setSnackbar({ open: true, message: 'Failed to add service.', severity: 'error' });
        },
        onSettled() {
            setAddedServicePlaceholder({ title: '', description: '', price: 0, time: 0, employees: [] }); // Clear input fields
            handleAddServiceDialogClose();
        },
    });

    const isLoading = allEmployeesQuery.isLoading || servicesQuery.isLoading ||
        onAddServiceMutation.isPending || onSaveMutation.isPending ||
        deleteServiceMutation.isPending ||
        businessConfigQuery.isLoading || updateServiceCategoriesMutation.isPending;
    const isError = allEmployeesQuery.isError || servicesQuery.isError ||
        onAddServiceMutation.isError || onSaveMutation.isError ||
        deleteServiceMutation.isError ||
        businessConfigQuery.isError || updateServiceCategoriesMutation.isError;

    const handleAddServiceClick = () => {
        setAddDialogErrors({});
        setAddedServicePlaceholder({ ...EMPTY_SERVICE_PLACEHOLDER });
        setAddedServiceImagePlaceholder(undefined);
        setIsAddServiceDialogOpen(true);
    };

    const handleAddServiceDialogClose = () => {
        setIsAddServiceDialogOpen(false);
        setAddDialogErrors({});
        setAddedServicePlaceholder({ ...EMPTY_SERVICE_PLACEHOLDER });
        setAddedServiceImagePlaceholder(undefined);
    };

    const handleEmployeeChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        setAddedServicePlaceholder((prev) => ({
            ...prev,
            employees: typeof value === 'string' ? value.split(',') : value,
        }));
        if (addDialogErrors.employees) {
            setAddDialogErrors((prev) => ({ ...prev, employees: undefined }));
        }
    };

    const validateFields = () => {
        const { title, price, employees, time } = addedServicePlaceholder;
        const errors: typeof addDialogErrors = {};
        const titleValue = title ?? '';

        if (!titleValue.trim()) {
            errors.title = 'Title is required.';
        } else if (titleValue.trim().length > MAX_SERVICE_TITLE_LENGTH) {
            errors.title = 'Title must be 30 characters or fewer.';
        }

        if (!price || price <= 0) {
            errors.price = 'Price must be greater than 0.';
        }

        if (!time || time <= 0) {
            errors.time = 'Duration is required.';
        }

        if (!employees.length) {
            errors.employees = 'Select at least one employee.';
        }

        setAddDialogErrors(errors);
        if (errors.employees && employeesErrorRef.current) {
            employeesErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return Object.keys(errors).length === 0;
    };

    const handleAddService = () => {
        if (validateFields()) {
            onAddServiceMutation.mutate();
        }
    };

    const handleDurationChange = (increment: boolean) => {
        setAddedServicePlaceholder((prev) => ({
            ...prev,
            time: increment ? prev.time + TIME_INTERVAL : Math.max(prev.time - TIME_INTERVAL, 0),
        }));
        if (addDialogErrors.time) {
            setAddDialogErrors((prev) => ({ ...prev, time: undefined }));
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (isError) return <Typography color="error" align="center">Failed to load data.</Typography>;

    // The following constants are used for the Select component
    // for selecting an employee for the service
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };



    return (
        <Box sx={{ pb: isMobile ? 10 : 0 }}>
            <Container>
                {!isMobile && (
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddServiceClick}
                            sx={{
                                borderRadius: '4px',
                                border: '2px solid rgba(255,255,255,0.15)',
                                fontWeight: '600',
                                paddingX: 3.5,
                            }}
                        >
                            Add Service
                        </Button>
                    </Box>
                )}

                {!isMobile && (
                    <hr
                        style={{
                            border: "none",
                            height: "2px",
                            backgroundColor: "rgba(0,0,0,0.2)",
                            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                            marginBottom: "20px",
                        }}
                    />
                )}
                {/* === Category Section === */}
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" fontWeight={600}>Categories</Typography>

                            {(businessConfigQuery.data?.preferences?.serviceCategories?.length ?? 0) > 0 && (
                                <IconButton
                                    size="small"
                                    onClick={() => setEditMode((prev) => !prev)}
                                    sx={{ p: 0.5 }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                            )}

                            <IconButton
                                size="small"
                                onClick={() => setIsAddCategoryDialogOpen(true)}
                                sx={{ p: 0.5 }}
                            >
                                <AddCircle fontSize="medium" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        {(businessConfigQuery.data?.preferences?.serviceCategories || []).map((cat, idx) => {
                            const isSelected = cat === selectedCategory && !editMode;
                            return (
                                <Chip
                                    key={idx}
                                    label={cat}
                                    onClick={
                                        editMode
                                            ? undefined
                                            : () => setSelectedCategory(isSelected ? null : cat)
                                    }
                                    onDelete={
                                        editMode
                                            ? () => {
                                                setCategoryToDelete(cat);
                                                setDeleteCategoryConfirmOpen(true);
                                            }
                                            : undefined
                                    }
                                    variant="filled"
                                    sx={{
                                        fontWeight: 500,
                                        textTransform: 'capitalize',
                                        borderRadius: '16px',
                                        cursor: editMode ? 'default' : 'pointer',
                                        backgroundColor: isSelected ? theme.palette.secondary.main : 'black',
                                        color: isSelected ? theme.palette.secondary.contrastText : 'white',
                                        '& .MuiChip-deleteIcon': {
                                            color: isSelected ? theme.palette.secondary.contrastText : 'white',
                                        },
                                        // 💡 REMOVE focus/hover highlight
                                        '&:hover': {
                                            backgroundColor: isSelected ? theme.palette.secondary.main : 'black',
                                        },
                                        '&.Mui-focusVisible': {
                                            backgroundColor: isSelected ? theme.palette.secondary.main : 'black',
                                        },
                                        '&.MuiChip-clickable:focus': {
                                            backgroundColor: isSelected ? theme.palette.secondary.main : 'black',
                                        },
                                    }}
                                />
                            );
                        })}

                        {!editMode && selectedCategory && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectedCategory(null)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: '16px',
                                    px: 2,
                                    height: '32px',
                                    color: theme.palette.secondary.main,
                                    borderColor: theme.palette.secondary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.secondary.main,
                                        color: theme.palette.getContrastText(theme.palette.secondary.main),
                                    },
                                }}
                            >
                                Clear Filter
                            </Button>


                        )}
                    </Box>
                </Box>


                <EditableServicesSection
                    services={servicesQuery.data || []}
                    onSaveService={(oldService, updatedService, image) => {
                        onSaveMutation.mutate({ oldService, updatedService, serviceImage: image });
                    }}
                    onDeleteService={(service) => deleteServiceMutation.mutate(service)}
                    employees={selectableEmployees}
                    categoryFilter={selectedCategory || null}
                    resourcePluralLabel={resourcePlural}
                />

                {/* Add service dialog */}
                <Dialog
                    open={isAddServiceDialogOpen}
                    onClose={handleAddServiceDialogClose}
                    fullWidth
                    maxWidth="sm"
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }} />
                            <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                                Add Service
                            </Typography>
                            <IconButton onClick={handleAddServiceDialogClose} aria-label="Close">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                            <ImageSelect
                                initialImagePath={undefined}
                                onImageChange={(file) => setAddedServiceImagePlaceholder(file)}
                                enabled={true}
                                altText="Service Image" />
                        </Box>
                        <Box>
                            <TextField
                                label="Title"
                                fullWidth
                                margin="dense"
                                name="title"
                                value={addedServicePlaceholder.title}
                                onChange={(e) => {
                                    setAddedServicePlaceholder({ ...addedServicePlaceholder, title: e.target.value });
                                    if (addDialogErrors.title) {
                                        setAddDialogErrors((prev) => ({ ...prev, title: undefined }));
                                    }
                                }}
                                inputProps={{ maxLength: MAX_SERVICE_TITLE_LENGTH }}
                                helperText={
                                    addDialogErrors.title ??
                                    `${(addedServicePlaceholder.title ?? '').length}/${MAX_SERVICE_TITLE_LENGTH}`
                                }
                                error={Boolean(addDialogErrors.title)}
                            />
                        </Box>
                        <Box>
                            <TextField
                                label="Description"
                                fullWidth
                                margin="dense"
                                multiline
                                rows={3}
                                name="description"
                                value={addedServicePlaceholder.description}
                                onChange={(e) => {
                                    setAddedServicePlaceholder({ ...addedServicePlaceholder, description: e.target.value });
                                }}
                                inputProps={{ maxLength: MAX_SERVICE_DESCRIPTION_LENGTH }}
                                helperText={`${(addedServicePlaceholder.description ?? '').length}/${MAX_SERVICE_DESCRIPTION_LENGTH}`}
                            />
                        </Box>
                        <Box>
                            <TextField
                                label="Price"
                                fullWidth
                                margin="dense"
                                type="text" // Change to text to remove the arrows
                                name="price"
                                value={addedServicePlaceholder.price}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    setAddedServicePlaceholder({ ...addedServicePlaceholder, price: isNaN(value) ? 0 : value });
                                    if (addDialogErrors.price) {
                                        setAddDialogErrors((prev) => ({ ...prev, price: undefined }));
                                    }
                                }}
                                error={Boolean(addDialogErrors.price)}
                                helperText={addDialogErrors.price}
                            />
                        </Box>
                        <Box>
                            <FormControl fullWidth margin="dense">
                                <InputLabel id="category-label">Category</InputLabel>
                                <Select
                                    labelId="category-label"
                                    value={addedServicePlaceholder.category || ''}
                                    onChange={(e) =>
                                        setAddedServicePlaceholder((prev) => ({
                                            ...prev,
                                            category: e.target.value,
                                        }))
                                    }
                                    input={<OutlinedInput label="Category" />}
                                >
                                    {(businessConfigQuery.data?.preferences?.serviceCategories || []).map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                            {cat}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconButton onClick={() => handleDurationChange(false)}>
                                <RemoveCircle />
                            </IconButton>
                            <TextField
                                label="Duration"
                                value={formatMinutes(addedServicePlaceholder.time)}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                                fullWidth
                                sx={{ textAlign: 'center' }}
                                error={Boolean(addDialogErrors.time)}
                                helperText={addDialogErrors.time}
                            />
                            <IconButton onClick={() => handleDurationChange(true)}>
                                <AddCircle />
                            </IconButton>
                        </Box>
                        <Box ref={employeesErrorRef}>
                            <FormControl fullWidth margin="dense" error={Boolean(addDialogErrors.employees)}>
                                <InputLabel id="employees-label">{resourcePlural}</InputLabel>
                                <Select
                                    labelId="employees-label"
                                    multiple
                                    value={addedServicePlaceholder.employees}
                                    onChange={handleEmployeeChange}
                                    input={<OutlinedInput label={resourcePlural} />}
                                    renderValue={(selected) => selected.map(id => selectableEmployees.find(emp => emp.id === id)?.firstName ?? '').filter(Boolean).join(', ')}
                                    MenuProps={MenuProps}
                                >
                                    {selectableEmployees.map((employee) => (
                                        <MenuItem key={employee.id} value={employee.id}>
                                            <Checkbox checked={addedServicePlaceholder.employees.indexOf(employee.id) > -1} />
                                            <ListItemText primary={employee.firstName} />
                                        </MenuItem>
                                    ))}
                                </Select>
                                {addDialogErrors.employees && (
                                    <FormHelperText>{addDialogErrors.employees}</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            px: 2,
                            py: 1,
                            gap: 0,
                            justifyContent: 'space-between',
                            '& > :not(:first-of-type)': { ml: 0 },
                        }}
                    >
                        <Button onClick={handleAddServiceDialogClose} sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddService}
                            color="primary"
                            sx={{
                                flex: 1,
                                borderRadius: '4px',
                                py: 1.2,
                                ml: 0,
                                bgcolor: 'common.black',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'grey.900' },
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={isAddCategoryDialogOpen} onClose={() => setIsAddCategoryDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }} />
                            <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                                Add New Category
                            </Typography>
                            <IconButton onClick={() => setIsAddCategoryDialogOpen(false)} aria-label="Close">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Box mt={1} mb={2}>
                            <TextField
                                label="Category"
                                fullWidth
                                margin="dense"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            px: 2,
                            py: 1,
                            gap: 0,
                            justifyContent: 'space-between',
                            '& > :not(:first-of-type)': { ml: 0 },
                        }}
                    >
                        <Button onClick={() => setIsAddCategoryDialogOpen(false)} sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                const current = businessConfigQuery.data?.preferences?.serviceCategories || [];
                                const trimmed = newCategory.trim();
                                if (trimmed && !current.includes(trimmed)) {
                                    updateServiceCategoriesMutation.mutate([...current, trimmed]);
                                } else {
                                    alert("Category is empty or already exists.");
                                }
                            }}
                            disabled={!newCategory.trim()}
                            sx={{
                                flex: 1,
                                borderRadius: '4px',
                                py: 1.2,
                                ml: 0,
                                bgcolor: 'common.black',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'grey.900' },
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>

                <ConfirmationDialog
                    open={deleteCategoryConfirmOpen}
                    onClose={() => setDeleteCategoryConfirmOpen(false)}
                    title="Confirm Deletion"
                    message={`Are you sure you want to delete "${categoryToDelete}"? This will also uncategorize any services that used it.`}
                    onConfirm={() => {
                        if (categoryToDelete) {
                            deleteCategoryMutation.mutate(categoryToDelete);
                            setDeleteCategoryConfirmOpen(false);
                            setCategoryToDelete(null);
                        }
                    }}
                    confirmButtonLabel="Delete"
                    cancelButtonLabel="Cancel"
                    confirmButtonSx={{ color: 'error.main' }}
                    cancelButtonSx={{ color: 'common.black' }}
                />


                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {isMobile && (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            px: 2,
                            py: 1,
                            bgcolor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 3,
                            zIndex: 10,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddServiceClick}
                            fullWidth
                            sx={{
                                borderRadius: '4px',
                                border: '2px solid rgba(255,255,255,0.15)',
                                fontWeight: '600',
                            }}
                        >
                            Add Service
                        </Button>
                    </Box>
                )}

            </Container>
        </Box>
    );
};

export default ServicesScreen;
